import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef } from "react";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

export default function SignIn() {
  const navigate = useNavigate();
  const { refresh, account, loading, claimPending } = useAuth();
  const [step, setStep] = useState("options"); // options | email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submittingRef = useRef(false);

  // Already signed in? Don't show the sign-in options — go to My Adaptience.
  // Guard: skip while an email verify + claim is in flight so we don't clobber the
  // post-claim navigate('/my-adaptience?claimed=1') and strip the success flag.
  useEffect(() => {
    if (!loading && account && !submittingRef.current) navigate("/my-adaptience", { replace: true });
  }, [loading, account, navigate]);

  const startGoogle = async () => {
    setError("");
    try {
      const cfg = await fetch(`${API}/auth/google/config`).then((r) => r.json());
      if (!cfg.configured || !cfg.client_id) {
        setError("Google sign-in isn't available just yet — please continue with email.");
        return;
      }
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUri = window.location.origin + "/auth/google";
      // Backend generates the OAuth state and sets it in a short-lived HttpOnly cookie;
      // we include the same value in the Google request and keep a client-side copy for a
      // fast-fail check on return. Backend re-validates state vs the cookie before exchange.
      const stateRes = await fetch(`${API}/auth/google/state`, { method: "POST", credentials: "include" });
      if (!stateRes.ok) {
        setError("Something went wrong starting Google sign-in. Please try email.");
        return;
      }
      const { state } = await stateRes.json();
      sessionStorage.setItem("adaptience_oauth_state", state);
      const p = new URLSearchParams({
        client_id: cfg.client_id,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        state,
        access_type: "offline",
        prompt: "select_account",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${p.toString()}`;
    } catch {
      setError("Something went wrong starting Google sign-in. Please try email.");
    }
  };

  const requestCode = async () => {
    setError("");
    if (!EMAIL_RE.test(email.trim())) { setError("Please enter a valid email address."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/email/request-code`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (r.status === 429) { setError("Too many requests. Please wait a few minutes and try again."); }
      else if (!r.ok) { setError("We couldn't send your code. Please try again."); }
      else { setStep("code"); }
    } catch {
      setError("We couldn't send your code. Please try again.");
    }
    setBusy(false);
  };

  const verifyCode = async () => {
    setError("");
    if (!/^\d{6}$/.test(code.trim())) { setError("Enter the 6-digit code from your email."); return; }
    setBusy(true);
    submittingRef.current = true;  // block the 'already signed in' redirect during verify+claim
    try {
      const r = await fetch(`${API}/auth/email/verify`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      if (r.status === 429) { setError("Too many attempts. Please request a new code."); submittingRef.current = false; setBusy(false); return; }
      if (!r.ok) { setError("That code is invalid or expired. Please try again."); submittingRef.current = false; setBusy(false); return; }
      await refresh();
      const claim = await claimPending();
      navigate(claim && claim.ok ? "/my-adaptience?claimed=1" : "/my-adaptience", { replace: true });
    } catch {
      setError("Something went wrong. Please try again.");
      submittingRef.current = false;
    }
    setBusy(false);
  };

  return (
    <div className="relative min-h-[100dvh] w-full bg-[#0a0a0a]">
      <Navbar />
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute -left-24 top-10 h-[46vh] w-[46vh] rounded-full blur-[130px]" style={{ background: "rgba(79,163,217,0.10)" }} />
      </div>
      <div className="relative z-10 mx-auto flex w-full max-w-md flex-col px-6 py-16 sm:py-24" data-testid="signin-page">
        <span className="text-[11px] font-medium uppercase tracking-[0.28em] text-white/50">Adaptience&trade;</span>
        <h1 className="mb-2 mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">Sign in to Adaptience</h1>
        <p className="mb-10 text-sm leading-relaxed text-white/50">
          Practise first. Create an account when you want to keep your journey.
        </p>

        {step === "options" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="signin-options">
            <button
              onClick={startGoogle}
              data-testid="google-signin-btn"
              className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white/[0.03] px-6 py-3.5 text-sm font-medium text-white transition-colors hover:bg-white/[0.07]"
            >
              <GoogleGlyph /> Continue with Google
            </button>
            <button
              onClick={() => { setStep("email"); setError(""); }}
              data-testid="email-signin-btn"
              className="flex w-full items-center justify-center gap-3 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: ACCENT }}
            >
              <Mail size={17} /> Continue with email
            </button>
            {error && <p className="pt-1 text-sm text-red-300/80" data-testid="signin-error">{error}</p>}
            <p className="pt-4 text-center text-sm text-white/45">
              No password needed — we'll email you a one-time code.
            </p>
          </motion.div>
        )}

        {step === "email" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="signin-email-step">
            <label className="block text-sm font-medium text-white/80">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && requestCode()}
              placeholder="you@example.com"
              autoFocus
              data-testid="signin-email-input"
              className="w-full rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-base text-white placeholder:text-white/30 focus:border-[#4FA3D9] focus:outline-none"
            />
            {error && <p className="text-sm text-red-300/80" data-testid="signin-error">{error}</p>}
            <button
              onClick={requestCode}
              disabled={busy}
              data-testid="send-code-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: ACCENT }}
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} Send me a code
            </button>
            <button onClick={() => { setStep("options"); setError(""); }} data-testid="signin-back-btn" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white/90">
              <ArrowLeft size={15} /> Back
            </button>
          </motion.div>
        )}

        {step === "code" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="signin-code-step">
            <p className="text-sm text-white/60">
              We sent a 6-digit code to <span className="text-white/85">{email}</span>. Enter it below.
            </p>
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && verifyCode()}
              placeholder="••••••"
              autoFocus
              data-testid="signin-code-input"
              className="w-full rounded-xl border border-white/12 bg-black/30 px-4 py-3 text-center text-2xl tracking-[0.5em] text-white placeholder:text-white/20 focus:border-[#4FA3D9] focus:outline-none"
            />
            {error && <p className="text-sm text-red-300/80" data-testid="signin-error">{error}</p>}
            <button
              onClick={verifyCode}
              disabled={busy}
              data-testid="verify-code-btn"
              className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: ACCENT }}
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} Verify &amp; continue
            </button>
            <div className="flex items-center justify-between">
              <button onClick={() => { setStep("email"); setCode(""); setError(""); }} data-testid="code-back-btn" className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white/90">
                <ArrowLeft size={15} /> Change email
              </button>
              <button onClick={requestCode} disabled={busy} data-testid="resend-code-btn" className="text-sm text-white/55 hover:text-white/90">
                Resend code
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
